import React, { useContext, useEffect, useRef, useState } from "react"
import { Link } from "gatsby"
import { css } from "@emotion/core"

import BlockSelector from "../../templates/components/BlockSelector"
import { PageContext } from "../../Contexts/PageContext"
import { addPageContext } from "../../scripts/addPageContext"

const subtleHoverStyle = css`
  transition: background-color 150ms ease, box-shadow 150ms ease;

  &:hover,
  &:focus-visible {
    background-color: #0a8792;
    box-shadow: 0 3px 8px rgba(0, 0, 0, 0.2);
  }
`

const dialogStyle = css`
  width: min(960px, calc(100vw - 32px));
  height: min(800px, calc(100vh - 32px));
  padding: 0;
  border: 0;
  border-radius: 6px;
  box-shadow: 0 12px 36px rgba(0, 0, 0, 0.35);
  overflow: hidden;
  background: #fff;

  &::backdrop {
    background: rgba(0, 0, 0, 0.65);
  }
`

const dialogHeaderStyle = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 64px;
  padding: 12px 16px 12px 24px;
  border-bottom: 1px solid #ddd;
`

const dialogTitleStyle = css`
  margin: 0;
  font-size: 22px;
  line-height: 1.3;
`

const closeButtonStyle = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: #155d91;
  font: inherit;
  font-size: 30px;
  line-height: 1;
  cursor: pointer;

  &:hover,
  &:focus {
    background: #f1f1f1;
  }

  &:focus {
    outline: 3px solid rgba(31, 127, 200, 0.35);
    outline-offset: 2px;
  }
`

const dialogFrameStyle = css`
  display: block;
  width: 100%;
  height: calc(100% - 64px);
  border: 0;
`

const addReferrer = destination => {
  const targetUrl = new URL(destination, window.location.origin)
  const currentUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`

  targetUrl.searchParams.set("referrer", currentUrl)

  return `${targetUrl.pathname}${targetUrl.search}${targetUrl.hash}`
}

const NewTabLink = ({ destination, includeReferrer, htmlProps, children }) => {
  const [href, setHref] = useState(destination)

  useEffect(() => {
    setHref(includeReferrer ? addReferrer(destination) : destination)
  })

  return (
    <a {...htmlProps} href={href} target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  )
}

const ModalLink = ({ stateProps, htmlProps, children, pageState }) => {
  const dialogRef = useRef(null)
  const [hasOpened, setHasOpened] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const href = addPageContext(stateProps.href, pageState)
  const dialogId = `${stateProps.id}-dialog`
  const titleId = `${dialogId}-title`

  const openModal = event => {
    const dialog = dialogRef.current
    if (!dialog || typeof dialog.showModal !== "function") return

    event.preventDefault()
    setHasOpened(true)
    setIsOpen(true)
    dialog.showModal()
  }

  const closeModal = () => {
    if (dialogRef.current) dialogRef.current.close()
  }

  const closeFromBackdrop = event => {
    if (event.target === dialogRef.current) closeModal()
  }

  return (
    <>
      <a
        {...htmlProps}
        css={stateProps.subtleHover ? subtleHoverStyle : undefined}
        href={href}
        aria-controls={dialogId}
        aria-expanded={isOpen}
        aria-haspopup="dialog"
        onClick={openModal}
      >
        {children}
      </a>
      <dialog
        ref={dialogRef}
        id={dialogId}
        css={dialogStyle}
        aria-labelledby={titleId}
        onClick={closeFromBackdrop}
        onClose={() => setIsOpen(false)}
      >
        <header css={dialogHeaderStyle}>
          <h2 id={titleId} css={dialogTitleStyle}>
            {stateProps.modalTitle}
          </h2>
          <button
            type="button"
            css={closeButtonStyle}
            aria-label={stateProps.modalCloseLabel}
            title={stateProps.modalCloseLabel}
            onClick={closeModal}
          >
            <span aria-hidden="true">&times;</span>
          </button>
        </header>
        {hasOpened && (
          <iframe
            css={dialogFrameStyle}
            src={href}
            title={stateProps.modalTitle}
          />
        )}
      </dialog>
    </>
  )
}

function linkTypeSelector(stateProps, htmlProps, children, pageState) {
  switch (stateProps.linkType) {
    case "gatsby":
      if (stateProps.target === "_blank")
        return (
          <NewTabLink
            destination={addPageContext(stateProps.to, pageState)}
            includeReferrer={stateProps.includeReferrer !== false}
            htmlProps={htmlProps}
          >
            {children}
          </NewTabLink>
        )

      return (
        <Link {...htmlProps} to={addPageContext(stateProps.to, pageState)}>
          {children}
        </Link>
      )
    case "html":
      let htmlPropsCopy = { ...htmlProps }
      if (stateProps.download && stateProps.download.length > 0)
        htmlPropsCopy.download = stateProps.download
      return (
        <a
          {...htmlPropsCopy}
          css={stateProps.subtleHover ? subtleHoverStyle : undefined}
          href={addPageContext(stateProps.href, pageState)}
          target={stateProps.target}
          rel={
            stateProps.target === "_blank" ? "noopener noreferrer" : undefined
          }
        >
          {children}
        </a>
      )
    default:
      return <a href="#">Error in link</a>
  }
}

export const LinkBlock = ({ stateProps, htmlProps, children }) => {
  const [pageState] = useContext(PageContext)

  if (stateProps.openInModal)
    return (
      <ModalLink
        stateProps={stateProps}
        htmlProps={htmlProps}
        pageState={pageState}
      >
        {children}
      </ModalLink>
    )

  return linkTypeSelector(stateProps, htmlProps, children, pageState)
}
LinkBlock.displayName = "Link"

export const LinkCanvas = ({ stateProps, htmlProps, children }) => {
  const noChild = <BlockSelector selected={stateProps.id} />

  let style = css`
    padding: 5px 0;
    border: 1px dashed orange;
  `

  return (
    <div css={style} {...htmlProps}>
      <a>{children ? children : noChild}</a>
    </div>
  )
}
LinkCanvas.displayName = "LinkCanvas"
